"use client";

import { FormEvent, useMemo, useState } from "react";
import { useFeedbackState } from "../../../components/ui/ToastProvider";

import type { CardDataForHash } from "../../../services/bookingPaymentClient";
import { useCardToken } from "../hooks/useCardToken";

type CardTokenModalProps = {
  open: boolean;
  onClose: () => void;
  onToken?: (token: string) => void;
};

const initialCardState = {
  cardNumber: "",
  expirationMonth: "",
  expirationYear: "",
  securityCode: "",
  holderName: "",
  docNumber: "",
  docType: "CPF",
};

export function CardTokenModal({ open, onClose, onToken }: CardTokenModalProps) {
  const [card, setCard] = useState(initialCardState);
  const { createToken, data, type, error, loading, reset } = useCardToken();
  const [formError, setFormError] = useFeedbackState<string | null>(null);
  const [successMessage, setSuccessMessage] = useFeedbackState<string | null>(
    null,
    "success"
  );

  const maskedToken = useMemo(() => {
    if (!data) return null;
    if (data.length <= 8) return data;
    return `${data.slice(0, 6)}...${data.slice(-4)}`;
  }, [data]);

  if (!open) return null;

  const updateCard = (field: keyof typeof card, value: string) => {
    setCard((prev) => ({ ...prev, [field]: value }));
  };

  const closeAndReset = () => {
    reset();
    setCard(initialCardState);
    setFormError(null);
    setSuccessMessage(null);
    onClose();
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);
    setSuccessMessage(null);

    if (
      !card.cardNumber.trim() ||
      !card.expirationMonth.trim() ||
      !card.expirationYear.trim() ||
      !card.securityCode.trim() ||
      !card.holderName.trim()
    ) {
      setFormError("Preencha todos os campos do cartão.");
      return;
    }

    const expirationMonth = card.expirationMonth.padStart(2, "0");
    const cardData: CardDataForHash = {
      card_number: card.cardNumber.replace(/\s+/g, ""),
      card_holder_name: card.holderName,
      card_expiration_date: `${expirationMonth}${card.expirationYear.slice(-2)}`,
      card_cvv: card.securityCode,
    };

    try {
      const token = await createToken(cardData);
      setSuccessMessage("Token gerado com sucesso.");
      onToken?.(token);
      setCard((prev) => ({ ...prev, securityCode: "" }));
    } catch (err: any) {
      setFormError(err?.message || "Não foi possível gerar o token.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="w-full max-w-xl rounded-lg bg-white p-6 shadow-xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Gerar token de cartão</h2>
            <p className="text-sm text-gray-600">
              Os dados ficam somente no navegador. Geramos um token seguro do cartão e
              enviamos ao backend.
            </p>
          </div>
          <button
            onClick={closeAndReset}
            className="rounded px-2 py-1 text-sm text-gray-500 transition hover:bg-gray-100"
            aria-label="Fechar modal"
          >
            ✕
          </button>
        </div>

        <form className="mt-4 space-y-3" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <label className="text-sm text-gray-700">Número do cartão</label>
            <input
              className="w-full rounded border px-3 py-2 focus:border-blue-500 focus:outline-none"
              placeholder="0000 0000 0000 0000"
              value={card.cardNumber}
              onChange={(e) => updateCard("cardNumber", e.target.value)}
              inputMode="numeric"
              autoComplete="cc-number"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <label className="text-sm text-gray-700">Mês de expiração</label>
              <input
                className="w-full rounded border px-3 py-2 focus:border-blue-500 focus:outline-none"
                placeholder="MM"
                maxLength={2}
                value={card.expirationMonth}
                onChange={(e) => updateCard("expirationMonth", e.target.value)}
                inputMode="numeric"
                autoComplete="cc-exp-month"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-gray-700">Ano de expiração</label>
              <input
                className="w-full rounded border px-3 py-2 focus:border-blue-500 focus:outline-none"
                placeholder="AAAA"
                maxLength={4}
                value={card.expirationYear}
                onChange={(e) => updateCard("expirationYear", e.target.value)}
                inputMode="numeric"
                autoComplete="cc-exp-year"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <label className="text-sm text-gray-700">CVV</label>
              <input
                className="w-full rounded border px-3 py-2 focus:border-blue-500 focus:outline-none"
                placeholder="123"
                maxLength={4}
                value={card.securityCode}
                onChange={(e) => updateCard("securityCode", e.target.value)}
                inputMode="numeric"
                autoComplete="cc-csc"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-gray-700">Nome do titular</label>
              <input
                className="w-full rounded border px-3 py-2 focus:border-blue-500 focus:outline-none"
                placeholder="Como está no cartão"
                value={card.holderName}
                onChange={(e) => updateCard("holderName", e.target.value)}
                autoComplete="cc-name"
              />
            </div>
          </div>

          <div className="grid grid-cols-[120px_1fr] gap-3">
            <div className="space-y-2">
              <label className="text-sm text-gray-700">Documento</label>
              <select
                className="w-full rounded border px-3 py-2 focus:border-blue-500 focus:outline-none"
                value={card.docType}
                onChange={(e) => updateCard("docType", e.target.value)}
              >
                <option value="CPF">CPF</option>
                <option value="CNPJ">CNPJ</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-sm text-gray-700">Número do documento</label>
              <input
                className="w-full rounded border px-3 py-2 focus:border-blue-500 focus:outline-none"
                placeholder="Somente números"
                value={card.docNumber}
                onChange={(e) => updateCard("docNumber", e.target.value)}
                inputMode="numeric"
              />
            </div>
          </div>

          {(formError || error) && (
            <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {formError || error}
            </div>
          )}

          {successMessage && data && (
            <div className="rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
              <div className="font-semibold">{successMessage}</div>
              {type && <div className="mt-1 text-xs text-green-700">Tipo: {type}</div>}
              <div className="mt-1 break-words font-mono text-xs text-green-900">
                {maskedToken}
              </div>
              <button
                type="button"
                className="mt-2 rounded bg-green-600 px-3 py-1 text-xs font-medium text-white transition hover:bg-green-700"
                onClick={() => data && navigator.clipboard?.writeText(data)}
              >
                Copiar token
              </button>
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={closeAndReset}
              className="rounded border px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-400"
              disabled={loading}
            >
              {loading ? "Gerando token..." : "Gerar token"}
            </button>
          </div>

          <p className="text-xs text-gray-500">
            Não enviamos os dados do cartão ao backend — apenas o token gerado pelo
            sistema de pagamento.
          </p>
        </form>
      </div>
    </div>
  );
}
